import React from "react";

import styled from "styled-components/macro";

import { useFamilyRegistrationContext } from "Signups/FamilyRegistration/FamilyRegistrationContext";
import MembershipStatusCheckboxItem from "Signups/FamilyRegistration/Shared/DragNDropCheckboxItem/MembershipStatusCheckboxItem";
import ErrorMessage from "Signups/FamilyRegistration/Shared/ErrorMessage";
import {
  Border,
} from "Signups/FamilyRegistration/Shared/StyledComponents";
import useFamilyRegistrationAdminPermissions from "Signups/FamilyRegistration/Shared/useFamilyRegistrationAdminPermissions";
import useUpdateSetting from "Signups/FamilyRegistration/Shared/useUpdateSetting";

import FnrDragNDrop from "UI/FnrDragNDrop";
import FnrLoadingBricks from "UI/FnrLoadingBricks";

import AddANewStatus from "./AddNewStatus";

export default function MembershipStatusAdminControls() {
  const {
    state: {
      familyRegistrationSettings,
      canEditMembershipStatusOptions,
      isSubnetwork,
      networkHasMembershipStatus,
    },
  } = useFamilyRegistrationContext();
  const { shouldSeparateMembershipStatusSection } =
    useFamilyRegistrationAdminPermissions();
  const { membershipStatus } = familyRegistrationSettings || {};
  const [dndItems, setDndItems] = React.useState();
  const { updateSetting, saving, error, setError } = useUpdateSetting();

  /** There was a bug that happened when you grabbed an item,
   * then moved your cursor out of the item, then moved the item down.
   * When you released the item, it would jump back to its original position
   * before it was saved and returned as an updated membershipStatus
   * and move the place you wanted to set it.
   * My solution was to temporarily display the orderedItems
   * returned from framer motion's reorder component to saveNewOrder. Then we new
   * */
  const saveNewOrder = async ({
    id,
    startPosition,
    endPosition,
    orderedItems,
  }) => {
    setDndItems(orderedItems);
    let distance = endPosition - startPosition;

    // Hit API: familyRegistrationSettings updates in context as part of this method
    await updateSetting({
      setting: "membershipStatus",
      itemID: id,
      value: distance,
      action: "reorder",
    });

    setDndItems(null);
  };

  const firstItem = membershipStatus?.statuses[0];
  const restOfItems = membershipStatus?.statuses?.slice(1);
  const showFamilyRegRelatedText =
    familyRegistrationSettings?.networkFamilyRegistration?.enabled;

  // Must have at least 2 enabled. If there are only 2 enabled, they can't disable any more.
  const preventDeselection =
    membershipStatus?.statuses.filter((status) => status.enabled).length <= 2;
  const existingPublicNames = membershipStatus?.statuses.map((status) => {
    return { ID: status.membershipStatusID, publicName: status.publicName };
  });

  if (!membershipStatus) return null;
  if (!networkHasMembershipStatus) return null;

  return (
    <>
      <Border style={{ maxWidth: "360px", padding: 6 }}>
        <Explainer>
          Admins can assign these statuses to a household via their member
          profile.{" "}
          {showFamilyRegRelatedText ? (
            <>
              The public family registration process will only show fields with
              checkmarks below. Those that select a household status will be
              added to your household database.
            </>
          ) : null}
        </Explainer>
        <div style={{ position: "relative" }}>
          <MembershipStatusCheckboxItem
            item={firstItem}
            style={{ marginBottom: "5px" }}
            canEditMembershipStatusOptions={canEditMembershipStatusOptions}
            disableStatusNameInput={isSubnetwork}
            hideCheckbox={shouldSeparateMembershipStatusSection}
          />
          <FnrDragNDrop
            items={dndItems || restOfItems}
            itemIdName="membershipStatusID"
            ItemComponent={MembershipStatusCheckboxItem}
            onRelease={saveNewOrder}
            disabled={saving}
            itemHasDragTrigger={true}
            canEditMembershipStatusOptions={canEditMembershipStatusOptions}
            hideCheckbox={shouldSeparateMembershipStatusSection}
            preventDeselection={preventDeselection}
            isLastDragableItem={
              dndItems?.length <= 1 || restOfItems?.length <= 1
            }
            existingPublicNames={existingPublicNames}
          />
          {canEditMembershipStatusOptions && (
            <AddANewStatus
              setError={setError}
              existingPublicNames={existingPublicNames}
            />
          )}
          {saving ? (
            <LoadingWrapper>
              <FnrLoadingBricks show={true} size={48} />
            </LoadingWrapper>
          ) : null}
        </div>
      </Border>
      <ErrorMessage error={error?.error} />
    </>
  );
}

const Explainer = styled.div`
  font-size: 13px;
  opacity: 0.8;
  padding: 4px 8px;
  padding-bottom: 10px;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: hsl(0, 0%, 100%, 0.5);
  display: grid;
  place-items: center;
`;
